import {
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataExchange } from './queryKeys';

type DataExchangeEligiblePerson = {
  id: UUID;
  email: string;
  firstName: string;
  lastName: string;
};

type DataExchangeEligiblePeopleResponse = {
  items: readonly DataExchangeEligiblePerson[];
  nextLink: string;
};

/**
 * Fetches all data exchange eligible people from the API.
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/get_eligible_responsible_persons_for_data_exchange_data_exchange__data_exchange_id__eligible_responsible_persons_get
 */
// fallow-ignore unused-export
export const fetchDataExchangeEligiblePeople = async (dataExchangeId: UUID) => {
  const response = await vestigasApi
    .get(
      ENDPOINT.DATA_EXCHANGE.GET_ELIGIBLE_RESPONSIBLE_PERSONS(dataExchangeId),
    )
    .json<DataExchangeEligiblePeopleResponse>();

  return (
    response?.items?.map((item) => ({
      ...item,
      name: [item.firstName, item.lastName].filter(Boolean).join(' '),
    })) ?? {
      items: [],
      nextLink: '',
    }
  );
};

/**
 * Generates the query options for the data exchanges eligible people query so they can be shared between the useQuery hook and the prefetching.
 */
export const getDataExchangeEligiblePeopleQueryOptions = ({
  dataExchangeId,
  options,
}: {
  dataExchangeId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<DataExchangeEligiblePeopleResponse>,
    'queryKey' | 'queryFn'
  >;
}) => {
  return {
    queryFn: dataExchangeId
      ? async () => fetchDataExchangeEligiblePeople(dataExchangeId)
      : skipToken,
    queryKey: queryKeysDataExchange.getAllEligiblePeople(dataExchangeId),
    staleTime: ms('60s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting all eligible people for the data exchange with the given id.
 */
export const useQueryDataExchangeEligiblePeople = (
  dataExchangeId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<DataExchangeEligiblePeopleResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    ...getDataExchangeEligiblePeopleQueryOptions({
      dataExchangeId,
      options,
    }),
  });
};
